import { isPastCalendarDay } from '../../utils/isPastCalendarDay';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CalendarGridItem } from '../../hooks/useCalendarData';
import { isJalaliPickerLocale } from '../../utils/calendarPickerLocale';
import {
  formatGregorianMonthYear,
  getDaysInGregorianMonth,
  gregorianMonthPad,
  gregorianWeekStartsOn,
  shiftGregorianMonth,
  todayGregorian,
  weekdayLabelsForGregorian,
} from '../../utils/gregorianCalendar';
import {
  getDaysInJalaliMonth,
  gregorianToJalali,
  gregorianWeekdaysToJalali,
  jalaliDateKey,
  jalaliMonths,
  jalaliToDate,
  jalaliToGregorian,
  parseJalaliDateKey,
  todayJalali,
  weekDaysShortFa,
} from '../../utils/jalali';
import type { CalendarWorkout } from '../../services/api';
import {
  NmPeaksCalendar,
  type NmPeaksCalendarDay,
  type NmPeaksWorkoutDot,
} from '../../components/NmPeaksCalendar';

function isWorkoutDone(w: CalendarWorkout): boolean {
  const actualTss = w.tss_actual != null ? Number(w.tss_actual) : null;
  const actualTime = w.total_time != null ? Number(w.total_time) : null;
  return !!(actualTime || actualTss);
}

function workoutTypeLabel(w: CalendarWorkout): string {
  return String(w?.workout_type?.name ?? w?.workout_type?.english_name ?? '').toLowerCase();
}

function dotTypeForWorkout(w: CalendarWorkout): NmPeaksWorkoutDot {
  if (!isWorkoutDone(w)) return 'planned';
  const name = workoutTypeLabel(w);
  if (name.includes('bike')) return 'bike';
  if (name.includes('strength')) return 'strength';
  return 'done';
}

function getCalendarDateKey(item?: CalendarGridItem): string | null {
  if (!item || item.day === '-') return null;
  return jalaliDateKey(item.year, item.month, item.day);
}

function jalaliMonthsCoveringGregorianMonth(gYear: number, gMonth: number) {
  const first = gregorianToJalali(gYear, gMonth, 1);
  const lastDay = getDaysInGregorianMonth(gYear, gMonth);
  const last = gregorianToJalali(gYear, gMonth, lastDay);
  const key = (y: number, m: number) => `${y}/${m}`;
  const map = new Map<string, { year: number; month: number }>();
  map.set(key(first.year, first.month), { year: first.year, month: first.month });
  map.set(key(last.year, last.month), { year: last.year, month: last.month });
  return [...map.values()];
}

interface Props {
  items: CalendarGridItem[];
  selectedDateKey: string | null;
  onDayPress: (globalIndex: number, dateKey?: string | null) => void;
  onPreviousMonth?: (year: number, month: number) => void;
  onNextMonth?: (year: number, month: number) => void;
}

/** Month grid + legend — mirrors Ai-Tempo VO2CalendarGrid + VO2PeaksCalendarFa. */
export function VO2CalendarGrid({
  items,
  selectedDateKey,
  onDayPress,
  onPreviousMonth,
  onNextMonth,
}: Props) {
  const { i18n } = useTranslation();
  const localeBase = i18n.language.split('-')[0] ?? 'en';
  const useJalali = isJalaliPickerLocale(i18n.language);
  const jalaliDate = todayJalali();
  const gregorianDate = todayGregorian();
  const weekStartsOn = gregorianWeekStartsOn(localeBase);

  const initialView = useMemo(() => {
    if (selectedDateKey) {
      const parsed = parseJalaliDateKey(selectedDateKey);
      if (parsed) {
        if (useJalali) return { year: parsed.year, month: parsed.month };
        const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
        return { year: g.year, month: g.month };
      }
    }
    if (useJalali) return { year: jalaliDate.year, month: jalaliDate.month };
    return { year: gregorianDate.year, month: gregorianDate.month };
  }, [
    gregorianDate.month,
    gregorianDate.year,
    jalaliDate.month,
    jalaliDate.year,
    selectedDateKey,
    useJalali,
  ]);

  const [viewYear, setViewYear] = useState(initialView.year);
  const [viewMonth, setViewMonth] = useState(initialView.month);
  const navigationTargetRef = useRef<{ month: number; year: number } | null>(null);

  useEffect(() => {
    setViewYear(initialView.year);
    setViewMonth(initialView.month);
    navigationTargetRef.current = null;
  }, [useJalali]);

  const selectedViewMonth = useMemo(() => {
    if (!selectedDateKey) return null;
    const parsed = parseJalaliDateKey(selectedDateKey);
    if (!parsed) return null;
    if (useJalali) return { year: parsed.year, month: parsed.month };
    const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
    return { year: g.year, month: g.month };
  }, [selectedDateKey, useJalali]);

  useEffect(() => {
    if (!selectedViewMonth) return;
    const { year: selectedYear, month: selectedMonth } = selectedViewMonth;
    if (selectedYear !== viewYear || selectedMonth !== viewMonth) {
      const navigationTarget = navigationTargetRef.current;
      if (
        navigationTarget &&
        navigationTarget.month === viewMonth &&
        navigationTarget.year === viewYear
      ) {
        return;
      }
      setViewYear(selectedYear);
      setViewMonth(selectedMonth);
    }
    const navigationTarget = navigationTargetRef.current;
    if (
      navigationTarget &&
      navigationTarget.month === selectedMonth &&
      navigationTarget.year === selectedYear
    ) {
      navigationTargetRef.current = null;
    }
  }, [selectedViewMonth, viewMonth, viewYear]);

  const dayByKey = useMemo(() => {
    const map = new Map<string, { item: CalendarGridItem; globalIdx: number }>();
    items.forEach((item, idx) => {
      const key = getCalendarDateKey(item);
      if (key) map.set(key, { item, globalIdx: idx });
    });
    return map;
  }, [items]);

  const calendarDaysBase = useMemo<NmPeaksCalendarDay[]>(() => {
    if (useJalali) {
      const byDay = new Map<number, { item: CalendarGridItem; globalIdx: number }>();
      items.forEach((item, globalIdx) => {
        if (item.month === viewMonth && item.year === viewYear && item.day !== '-') {
          byDay.set(Number(item.day), { item, globalIdx });
        }
      });

      const firstDay = jalaliToDate(viewYear, viewMonth, 1).getDay();
      const padCount = gregorianWeekdaysToJalali(firstDay);
      const prevYear = viewMonth === 1 ? viewYear - 1 : viewYear;
      const prevMonth = viewMonth === 1 ? 12 : viewMonth - 1;
      const prevMonthDays = getDaysInJalaliMonth(prevYear, prevMonth);
      const currentMonthDays = getDaysInJalaliMonth(viewYear, viewMonth);
      const result: NmPeaksCalendarDay[] = [];

      for (let i = padCount - 1; i >= 0; i--) {
        result.push({
          key: `${prevYear}-${prevMonth}-muted-${prevMonthDays - i}`,
          day: prevMonthDays - i,
          muted: true,
        });
      }

      for (let day = 1; day <= currentMonthDays; day++) {
        const entry = byDay.get(day);
        const item = entry?.item;
        const isToday =
          jalaliDate.year === viewYear && jalaliDate.month === viewMonth && jalaliDate.dat === day;
        const workouts = Array.isArray(item?.extra?.workouts) ? item.extra.workouts : [];
        const dots = workouts.slice(0, 3).map(dotTypeForWorkout);
        const dateKey = getCalendarDateKey(item);

        result.push({
          key: `${viewYear}-${viewMonth}-${day}-${entry?.globalIdx ?? day}`,
          day,
          today: isToday,
          selected: false,
          workouts: dots.length > 0 ? dots : undefined,
          hasCompletedWorkout: workouts.some(isWorkoutDone),
          hasPlannedWorkout: workouts.some(w => !isWorkoutDone(w)),
          isPast: isPastCalendarDay(dateKey, jalaliDate),
          payload: { globalIdx: entry?.globalIdx ?? -1, dateKey },
        });
      }

      const nextCells = (7 - (result.length % 7)) % 7;
      for (let day = 1; day <= nextCells; day++) {
        const nextYear = viewMonth === 12 ? viewYear + 1 : viewYear;
        const nextMonth = viewMonth === 12 ? 1 : viewMonth + 1;
        result.push({ key: `${nextYear}-${nextMonth}-muted-${day}`, day: '', muted: true });
      }

      return result;
    }

    const pad = gregorianMonthPad(viewYear, viewMonth, weekStartsOn);
    const prev = shiftGregorianMonth(viewYear, viewMonth, -1);
    const prevDays = getDaysInGregorianMonth(prev.year, prev.month);
    const daysInMonth = getDaysInGregorianMonth(viewYear, viewMonth);
    const result: NmPeaksCalendarDay[] = [];

    for (let i = pad - 1; i >= 0; i--) {
      result.push({
        key: `${prev.year}-${prev.month}-muted-${prevDays - i}`,
        day: prevDays - i,
        muted: true,
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const j = gregorianToJalali(viewYear, viewMonth, day);
      const dateKey = jalaliDateKey(j.year, j.month, j.dat);
      const entry = dayByKey.get(dateKey);
      const item = entry?.item;
      const isToday =
        gregorianDate.year === viewYear &&
        gregorianDate.month === viewMonth &&
        gregorianDate.day === day;
      const workouts = Array.isArray(item?.extra?.workouts) ? item.extra.workouts : [];
      const dots = workouts.slice(0, 3).map(dotTypeForWorkout);

      result.push({
        key: `${viewYear}-${viewMonth}-${day}-${entry?.globalIdx ?? day}`,
        day,
        today: isToday,
        selected: false,
        workouts: dots.length > 0 ? dots : undefined,
          hasCompletedWorkout: workouts.some(isWorkoutDone),
          hasPlannedWorkout: workouts.some(w => !isWorkoutDone(w)),
          isPast: isPastCalendarDay(dateKey, jalaliDate),
        payload: { globalIdx: entry?.globalIdx ?? -1, dateKey },
      });
    }

    const next = shiftGregorianMonth(viewYear, viewMonth, 1);
    const nextCells = (7 - (result.length % 7)) % 7;
    for (let day = 1; day <= nextCells; day++) {
      result.push({ key: `${next.year}-${next.month}-muted-${day}`, day, muted: true });
    }

    return result;
  }, [
    dayByKey,
    gregorianDate.day,
    gregorianDate.month,
    gregorianDate.year,
    items,
    jalaliDate,
    useJalali,
    viewMonth,
    viewYear,
    weekStartsOn,
  ]);

  const calendarDays = useMemo<NmPeaksCalendarDay[]>(
    () =>
      calendarDaysBase.map((d) => ({
        ...d,
        selected:
          !!(d.payload as { dateKey?: string })?.dateKey &&
          (d.payload as { dateKey?: string }).dateKey === selectedDateKey,
      })),
    [calendarDaysBase, selectedDateKey],
  );

  const scrollToFirstDayOf = useCallback(
    (month: number, year: number) => {
      const idx = items.findIndex((item) => {
        if (item.day === '-') return false;
        if (useJalali) return item.month === month && item.year === year;
        const g = jalaliToGregorian(item.year, item.month, Number(item.day));
        return g.year === year && g.month === month;
      });
      if (idx >= 0) onDayPress(idx, getCalendarDateKey(items[idx]));
    },
    [items, onDayPress, useJalali],
  );

  const hasMonthInData = useCallback(
    (month: number, year: number) =>
      items.some((item) => {
        if (item.day === '-') return false;
        if (useJalali) return item.month === month && item.year === year;
        const g = jalaliToGregorian(item.year, item.month, Number(item.day));
        return g.year === year && g.month === month;
      }),
    [items, useJalali],
  );

  const ensureViewMonthData = useCallback(
    (month: number, year: number, fetch?: (y: number, m: number) => void) => {
      if (!fetch) return;
      if (useJalali) {
        fetch(year, month);
        return;
      }
      jalaliMonthsCoveringGregorianMonth(year, month).forEach(({ year: jy, month: jm }) => {
        if (!items.some((item) => item.year === jy && item.month === jm && item.day !== '-')) {
          fetch(jy, jm);
        }
      });
    },
    [items, useJalali],
  );

  useEffect(() => {
    const navigationTarget = navigationTargetRef.current;
    if (!navigationTarget) return;
    const idx = items.findIndex((item) => {
      if (item.day === '-') return false;
      if (useJalali) {
        return item.month === navigationTarget.month && item.year === navigationTarget.year;
      }
      const g = jalaliToGregorian(item.year, item.month, Number(item.day));
      return g.year === navigationTarget.year && g.month === navigationTarget.month;
    });
    if (idx >= 0) {
      onDayPress(idx, getCalendarDateKey(items[idx]));
      navigationTargetRef.current = null;
    } else if (items.length > 0) {
      navigationTargetRef.current = null;
    }
  }, [items, onDayPress, useJalali]);

  const handleDayPress = useCallback(
    (day: NmPeaksCalendarDay) => {
      const { globalIdx = -1, dateKey = null } = (day.payload ?? {}) as {
        globalIdx?: number;
        dateKey?: string | null;
      };
      if (globalIdx >= 0) onDayPress(globalIdx, dateKey);
    },
    [onDayPress],
  );

  const handlePrevious = useCallback(() => {
    if (useJalali) {
      const newMonth = viewMonth === 1 ? 12 : viewMonth - 1;
      const newYear = viewMonth === 1 ? viewYear - 1 : viewYear;
      navigationTargetRef.current = { month: newMonth, year: newYear };
      setViewMonth(newMonth);
      setViewYear(newYear);
      if (!hasMonthInData(newMonth, newYear)) ensureViewMonthData(newMonth, newYear, onPreviousMonth);
      scrollToFirstDayOf(newMonth, newYear);
      return;
    }

    const shifted = shiftGregorianMonth(viewYear, viewMonth, -1);
    navigationTargetRef.current = { month: shifted.month, year: shifted.year };
    setViewMonth(shifted.month);
    setViewYear(shifted.year);
    if (!hasMonthInData(shifted.month, shifted.year)) {
      ensureViewMonthData(shifted.month, shifted.year, onPreviousMonth);
    }
    scrollToFirstDayOf(shifted.month, shifted.year);
  }, [
    ensureViewMonthData,
    hasMonthInData,
    onPreviousMonth,
    scrollToFirstDayOf,
    useJalali,
    viewMonth,
    viewYear,
  ]);

  const handleNext = useCallback(() => {
    if (useJalali) {
      const newMonth = viewMonth === 12 ? 1 : viewMonth + 1;
      const newYear = viewMonth === 12 ? viewYear + 1 : viewYear;
      navigationTargetRef.current = { month: newMonth, year: newYear };
      setViewMonth(newMonth);
      setViewYear(newYear);
      if (!hasMonthInData(newMonth, newYear)) ensureViewMonthData(newMonth, newYear, onNextMonth);
      scrollToFirstDayOf(newMonth, newYear);
      return;
    }

    const shifted = shiftGregorianMonth(viewYear, viewMonth, 1);
    navigationTargetRef.current = { month: shifted.month, year: shifted.year };
    setViewMonth(shifted.month);
    setViewYear(shifted.year);
    if (!hasMonthInData(shifted.month, shifted.year)) {
      ensureViewMonthData(shifted.month, shifted.year, onNextMonth);
    }
    scrollToFirstDayOf(shifted.month, shifted.year);
  }, [
    ensureViewMonthData,
    hasMonthInData,
    onNextMonth,
    scrollToFirstDayOf,
    useJalali,
    viewMonth,
    viewYear,
  ]);

  const weekdayLabels = useJalali ? weekDaysShortFa : weekdayLabelsForGregorian(localeBase);
  const title = useJalali
    ? (jalaliMonths[viewMonth] ?? '')
    : formatGregorianMonthYear(viewYear, viewMonth, localeBase);
  const subtitle = useJalali ? viewYear : undefined;

  return (
    <NmPeaksCalendar
      compact
      title={title}
      subtitle={subtitle}
      weekdayLabels={weekdayLabels}
      days={calendarDays}
      onDayPress={handleDayPress}
      onPrevious={handlePrevious}
      onNext={handleNext}
    />
  );
}
